import React from 'react';
import { capitalize } from '../utils/format';

/**
 * Color-coded status pills. Color families per spec:
 * pending gray · confirmed blue · processing amber · formulated orange ·
 * dispensing purple · ready_to_ship cyan · shipped indigo ·
 * delivered/completed green · cancelled red · refunded orange.
 * Meaning is never color-alone — the label always renders, which is what keeps
 * 'formulated' and the (rare, terminal) 'refunded' readable despite sharing the
 * orange family.
 */
const ORDER_STYLES: Record<string, string> = {
  pending:
    'bg-slate-500/10 text-slate-600 border-slate-500/30 dark:bg-slate-400/10 dark:text-slate-300 dark:border-slate-400/30',
  confirmed:
    'bg-blue-500/10 text-blue-600 border-blue-500/30 dark:bg-blue-400/10 dark:text-blue-300 dark:border-blue-400/30',
  processing:
    'bg-amber-500/10 text-amber-600 border-amber-500/30 dark:bg-amber-400/10 dark:text-amber-300 dark:border-amber-400/30',
  // Compounded and bottled, Shiprocket order created, waiting on a courier.
  formulated:
    'bg-orange-500/10 text-orange-600 border-orange-500/30 dark:bg-orange-400/10 dark:text-orange-300 dark:border-orange-400/30',
  dispensing:
    'bg-purple-500/10 text-purple-600 border-purple-500/30 dark:bg-purple-400/10 dark:text-purple-300 dark:border-purple-400/30',
  ready_to_ship:
    'bg-cyan-500/10 text-cyan-600 border-cyan-500/30 dark:bg-cyan-400/10 dark:text-cyan-300 dark:border-cyan-400/30',
  shipped:
    'bg-indigo-500/10 text-indigo-600 border-indigo-500/30 dark:bg-indigo-400/10 dark:text-indigo-300 dark:border-indigo-400/30',
  delivered:
    'bg-green-500/10 text-green-600 border-green-500/30 dark:bg-green-400/10 dark:text-green-300 dark:border-green-400/30',
  completed:
    'bg-teal-500/10 text-teal-600 border-teal-500/30 dark:bg-teal-400/10 dark:text-teal-300 dark:border-teal-400/30',
  cancelled:
    'bg-red-500/10 text-red-600 border-red-500/30 dark:bg-red-400/10 dark:text-red-300 dark:border-red-400/30',
  refunded:
    'bg-orange-500/10 text-orange-600 border-orange-500/30 dark:bg-orange-400/10 dark:text-orange-300 dark:border-orange-400/30',
};

const PAYMENT_STYLES: Record<string, string> = {
  pending: ORDER_STYLES.pending,
  paid: ORDER_STYLES.delivered,
  failed: ORDER_STYLES.cancelled,
  refunded: ORDER_STYLES.refunded,
  partially_refunded: ORDER_STYLES.refunded,
};

function pretty(status: string): string {
  return capitalize(status.replace(/_/g, ' '));
}

export function StatusBadge({ status, label }: { status: string; label?: string }) {
  const cls = ORDER_STYLES[status] ?? ORDER_STYLES.pending;
  return (
    <span
      className={`inline-flex items-center whitespace-nowrap rounded-full border px-2.5 py-0.5 text-xs font-medium ${cls}`}
    >
      {label ?? pretty(status)}
    </span>
  );
}

export function PaymentBadge({ status }: { status: string }) {
  const cls = PAYMENT_STYLES[status] ?? ORDER_STYLES.pending;
  return (
    <span
      className={`inline-flex items-center whitespace-nowrap rounded-full border px-2.5 py-0.5 text-xs font-medium ${cls}`}
    >
      {pretty(status)}
    </span>
  );
}
